import React, { useEffect, useRef, useState } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { GraduationCap, Users, HeartHandshake, RefreshCw, AlertCircle } from 'lucide-react';
import {
  DEMO_USER_VARIABLES,
  VoiceflowUserRole,
  VoiceflowUserVariables,
  VOICEFLOW_PROJECT_ID,
  buildLaunchPayload,
  buildVoiceflowConfig,
  destroyVoiceflowWidget,
  loadVoiceflowWidget,
} from 'helpers/voiceflow';

interface RoleOption {
  role: VoiceflowUserRole;
  label: string;
  app: string;
  description: string;
  icon: React.ReactNode;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    role: 'student',
    label: 'Student',
    app: 'LyfeGuide',
    description: 'What a student sees when they open Hope AI in LyfeGuide.',
    icon: <GraduationCap className="size-5" />,
  },
  {
    role: 'coach',
    label: 'Coach',
    app: 'LyfeCoach',
    description: 'What a coach sees when they open Hope AI in LyfeCoach.',
    icon: <Users className="size-5" />,
  },
  {
    role: 'parent',
    label: 'Parent',
    app: 'LyfeParent',
    description: 'What a parent sees when they open Hope AI in LyfeParent.',
    icon: <HeartHandshake className="size-5" />,
  },
];

type WidgetStatus = 'loading' | 'ready' | 'error';

const USER_FIELDS: { key: keyof VoiceflowUserVariables; label: string; type: string }[] = [
  { key: 'fname', label: 'First name', type: 'text' },
  { key: 'lname', label: 'Last name', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
];

const Chatbot = () => {
  const [role, setRole] = useState<VoiceflowUserRole>('student');
  // Bumped by "Restart conversation" to re-run the load effect for the same role.
  const [restartCount, setRestartCount] = useState(0);
  const [status, setStatus] = useState<WidgetStatus>('loading');
  const [error, setError] = useState<string>('');
  // `user` follows the form; `launchedUser` is what the running conversation
  // was started with. Voiceflow only reads variables on launch, so edits are
  // sent on the next restart or variant switch.
  const [user, setUser] = useState<VoiceflowUserVariables>(DEMO_USER_VARIABLES);
  const [launchedUser, setLaunchedUser] = useState<VoiceflowUserVariables>(DEMO_USER_VARIABLES);

  const containerRef = useRef<HTMLDivElement>(null);
  // Loads are chained rather than fired in parallel - otherwise a fast role
  // switch can leave two widgets rendered into the page.
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    let cancelled = false;
    // Captured now: the container is keyed on the same values as this effect,
    // so each run owns a freshly mounted node (see the render below).
    const target = containerRef.current;

    const mountWidget = async () => {
      if (cancelled || !target) {
        return;
      }

      setStatus('loading');
      setError('');

      const chat = await loadVoiceflowWidget();
      // `destroy` detaches the host element Voiceflow injected, which can take
      // the target with it, so a load is only safe against a live node.
      if (cancelled || !target.isConnected) {
        return;
      }

      destroyVoiceflowWidget();
      await chat.load(buildVoiceflowConfig(role, launchedUser, target));

      if (cancelled) {
        // The role changed while this load was in flight; the next queued load
        // would otherwise stack a second widget on top of this one.
        destroyVoiceflowWidget();
        return;
      }

      setStatus('ready');
    };

    queue.current = queue.current.then(mountWidget, mountWidget).catch((err) => {
      console.error('Error loading Voiceflow widget:', err);
      if (!cancelled) {
        setError(err instanceof Error ? err.message : 'Unable to load the assistant.');
        setStatus('error');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [role, restartCount, launchedUser]);

  // The widget attaches listeners outside React, so it is torn down explicitly
  // when the page unmounts rather than left behind on the next route.
  useEffect(() => destroyVoiceflowWidget, []);

  const activeOption = ROLE_OPTIONS.find((option) => option.role === role) as RoleOption;
  // Compared as sent (trimmed), so stray whitespace does not flag a change.
  const hasPendingChanges = (Object.keys(user) as (keyof VoiceflowUserVariables)[]).some(
    (key) => user[key].trim() !== launchedUser[key].trim()
  );

  const restart = () => {
    setLaunchedUser(user);
    setRestartCount((count) => count + 1);
  };

  const switchRole = (nextRole: VoiceflowUserRole) => {
    // Re-selecting the active variant would reload into the same container
    // node, which Voiceflow cannot attach to again; restarting handles that.
    if (nextRole === role) {
      return;
    }
    setLaunchedUser(user);
    setRole(nextRole);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Hope AI" pageTitle="Dashboard" />

      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 xl:col-span-4">
          <div className="card">
            <div className="card-body">
              <h6 className="mb-1 text-15">Assistant variant</h6>
              <p className="mb-4 text-sm text-slate-500 dark:text-zink-300">
                Preview Hope AI as each app's users see it. The variant is sent as{' '}
                <code className="text-custom-500">user_role</code>, with the demo
                user below.
              </p>

              <div className="flex flex-col gap-2">
                {ROLE_OPTIONS.map((option) => {
                  const isActive = option.role === role;

                  return (
                    <button
                      key={option.role}
                      type="button"
                      onClick={() => switchRole(option.role)}
                      aria-pressed={isActive}
                      className={`flex items-start gap-3 p-3 text-left transition-all duration-200 border rounded-md ${
                        isActive
                          ? 'border-custom-500 bg-custom-50 dark:bg-custom-500/10'
                          : 'border-slate-200 dark:border-zink-500 hover:border-custom-300 dark:hover:border-custom-500/50'
                      }`}
                    >
                      <span
                        className={`flex items-center justify-center rounded-md size-9 shrink-0 ${
                          isActive
                            ? 'bg-custom-500 text-white'
                            : 'bg-slate-100 dark:bg-zink-600 text-slate-500 dark:text-zink-200'
                        }`}
                      >
                        {option.icon}
                      </span>
                      <span className="grow">
                        <span
                          className={`block text-sm font-medium ${
                            isActive
                              ? 'text-custom-500'
                              : 'text-slate-800 dark:text-zink-100'
                          }`}
                        >
                          {option.label}
                        </span>
                        {/* All three descriptions are visible at once here, so
                            the cards carry only the app name and the fuller
                            sentence lives on the active variant's panel. */}
                        <span className="block text-xs text-slate-500 dark:text-zink-300">
                          {option.app}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={restart}
                disabled={status === 'loading'}
                className="w-full mt-4 text-custom-500 btn bg-custom-100 border-custom-100 hover:text-custom-600 hover:bg-custom-200 hover:border-custom-200 focus:text-custom-600 focus:bg-custom-200 focus:border-custom-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-custom-500/20 dark:border-transparent dark:hover:bg-custom-500/30 dark:focus:bg-custom-500/30"
              >
                <RefreshCw
                  className={`inline-block size-4 mr-1 ${
                    status === 'loading' ? 'animate-spin' : ''
                  }`}
                />
                <span className="align-middle">Restart conversation</span>
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="mb-1 text-15">Demo user</h6>
              <p className="mb-4 text-sm text-slate-500 dark:text-zink-300">
                Test values standing in for the signed-in user's details.
              </p>

              <div className="flex flex-col gap-3 mb-5">
                {USER_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label
                      htmlFor={`hope-user-${field.key}`}
                      className="block mb-1 text-sm font-medium"
                    >
                      {field.label}{' '}
                      <code className="text-xs font-normal text-custom-500">{field.key}</code>
                    </label>
                    <input
                      id={`hope-user-${field.key}`}
                      type={field.type}
                      value={user[field.key]}
                      onChange={(e) => setUser({ ...user, [field.key]: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
                    />
                  </div>
                ))}
              </div>

              <h6 className="mb-3 text-15">Launch payload</h6>
              <pre className="p-3 overflow-x-auto text-xs rounded-md bg-slate-100 dark:bg-zink-600 text-slate-700 dark:text-zink-100">
                {JSON.stringify(buildLaunchPayload(role, user), null, 2)}
              </pre>
              {hasPendingChanges && (
                <p className="mt-2 mb-0 text-xs text-yellow-600 dark:text-yellow-500">
                  Restart the conversation to send these changes to Hope.
                </p>
              )}
              <p className="mt-3 mb-0 text-xs text-slate-500 dark:text-zink-300">
                Project ID:{' '}
                <span className="font-mono break-all">{VOICEFLOW_PROJECT_ID}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="col-span-12 xl:col-span-8">
          <div className="card">
            <div className="flex items-center gap-3 card-body">
              <div className="grow">
                <h6 className="mb-1 text-15">{activeOption.app}</h6>
                <p className="mb-0 text-sm text-slate-500 dark:text-zink-300">
                  {activeOption.description}
                </p>
              </div>
              <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-custom-100 border-transparent text-custom-500 dark:bg-custom-500/20">
                user_role: {role}
              </span>
            </div>

            <div className="pt-0 card-body">
              <div className="relative overflow-hidden border rounded-md border-slate-200 dark:border-zink-500">
                {/* The widget renders into this element in embedded mode, so it
                    needs a concrete height of its own. The key gives every load
                    a brand new node: Voiceflow's teardown leaves the previous
                    target unusable, and re-loading into it fails to attach. */}
                <div
                  key={`${role}-${restartCount}`}
                  ref={containerRef}
                  className="h-[640px] w-full"
                />

                {status !== 'ready' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white dark:bg-zink-700">
                    {status === 'loading' ? (
                      <>
                        <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-custom-500"></div>
                        <p className="mb-0 text-sm text-slate-500 dark:text-zink-300">
                          Loading the {activeOption.app} preview...
                        </p>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="size-8 text-red-500" />
                        <p className="mb-0 text-sm text-slate-500 dark:text-zink-300">
                          {error || 'Unable to load the assistant.'}
                        </p>
                        <button
                          type="button"
                          onClick={restart}
                          className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:bg-custom-600 focus:border-custom-600"
                        >
                          Try again
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Chatbot;
